import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectNaceCodes } from 'store/slices/dataSets/selectors';
import {
  useCreateAgentSessionBomItemMutation,
  useDeleteAgentSessionBomItemMutation,
  useGetAgentSessionBomQuery,
  useStartAgentSessionReviewMutation,
  useUpdateAgentSessionBomItemMutation,
  useUpdateAgentSessionBomMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IUseAICreatorBoMRootParams from '../interfaces/IUseAICreatorBoMRootParams';

const useAICreatorBoMRoot = ({ session }: IUseAICreatorBoMRootParams) => {
  const { valueChainMapId } = useParams();

  const [updateAgentSessionBomItem] = useUpdateAgentSessionBomItemMutation();
  const [
    deleteAgentSessionBomItem,
    { isLoading: isDeleteAgentSessionBomItemLoading }] = useDeleteAgentSessionBomItemMutation();
  const [createAgentSessionBomItem] = useCreateAgentSessionBomItemMutation();
  const [updateAgentSessionBom] = useUpdateAgentSessionBomMutation();
  const [
    startAgentSessionReview,
    { isLoading: isStartAgentSessionReviewLoading },
  ] = useStartAgentSessionReviewMutation();

  const { data: naceCodesData } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const {
    data: agentSessionBom,
    isFetching: isAgentSessionBomFetching,
    refetch: refetchAgentSessionBom,
  } = useGetAgentSessionBomQuery({
    sessionId: session?.id || 0,
    valueChainMapId: valueChainMapId || '',
  }, {
    skip: !session?.id || !valueChainMapId,
  });

  const handleRefetchAgentSessionBom = useCallback(() => {
    if (!session?.id || !valueChainMapId) {
      return undefined;
    }

    return refetchAgentSessionBom();
  }, [refetchAgentSessionBom, session?.id, valueChainMapId]);

  const sessionUnitStatusByUnitId = useMemo(
    () => new Map(
      (agentSessionBom?.data || []).map((bom) => [bom.sessionUnit.unitId, bom.sessionUnit.status] as const),
    ),
    [agentSessionBom?.data],
  );

  return {
    valueChainMapId,
    naceCodesData,
    agentSessionBom,
    isAgentSessionBomFetching,
    refetchAgentSessionBom: handleRefetchAgentSessionBom,
    sessionUnitStatusByUnitId,
    updateAgentSessionBomItem,
    deleteAgentSessionBomItem,
    isDeleteAgentSessionBomItemLoading,
    createAgentSessionBomItem,
    updateAgentSessionBom,
    startAgentSessionReview,
    isStartAgentSessionReviewLoading,
  };
};

export default useAICreatorBoMRoot;
